import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLocationSelector from '../Components/CompanyLocationSelector';
import Sidebar from '../Components/Sidebar';
import AreaMultiSelect from '../Components/AreaMultiSelect';
import axios from '../api/axiosInstance';
import trident from '../assets/odin-trident.png';
import '../dashboard/dashboard.css';
import './AssetsDatabase.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import * as XLSXStyle from 'xlsx-js-style';
import { logout } from '../auth/cookieUtils';

const COVERED_PROCESS_OPTIONS = [
  'BMP',
  'EPN',
  'PSM',
  'RMP',
  'SARA 302',
  'SARA 312',
  'SARA 313',
  'SPC',
  'SPCC',
  'SRPV',
  'Hazard Contents (EPA/NFPA)',
  'Kraft Liquor Cycle',
];

const ASSET_TABLE_COLUMNS = [
  ['equipmentName', 'Equipment Name'],
  ['equipmentNumber', 'Equipment Number'],
  ['alternateNumber', 'Alternate Number'],
  ['areaId', 'Area'],
  ['equipmentType', 'Equipment Type'],
  ['riskRating', 'Risk Rating'],
  ['materialType', 'Material Type'],
  ['outOfService', 'Status'],
  ['nextExternalInspection', 'Next External Inspection'],
  ['nextInternalInspection', 'Next Internal Inspection'],
  ['nextUtInspection', 'Next UT Inspection'],
];


const AssetsDatabase = () => {
  const navigate = useNavigate();
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [areas, setAreas] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [areaIdNameMap, setAreaIdNameMap] = useState({});
  const [sortBy, setSortBy] = useState('equipmentName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0); // Backend pages usually start at 0
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0); // Optional, for "showing X of Y"
  const [pageSize, setPageSize] = useState(50);
  const [searchEquipmentName, setSearchEquipmentName] = useState('');
  const [searchEquipmentNumber, setSearchEquipmentNumber] = useState('');
  const [searchRiskRating, setSearchRiskRating] = useState('');
  const [searchPastDue, setSearchPastDue] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [selectedCoveredProcesses, setSelectedCoveredProcesses] = useState([]);
  const [showOutOfService, setShowOutOfService] = useState(false);



  const handleLogout = () => {
    logout();
  };


  
const clearSearchFilters = () => {
  setSelectedAreaIds([]);
  setSearchEquipmentName('');
  setSearchEquipmentNumber('');
  setSearchRiskRating('');
  setSearchPastDue(false);
  setShowOutOfService(false);
  setSelectedCoveredProcesses([]);
  setCurrentPage(0);
  setSearchTriggered(prev => !prev);
};

const handleLocationChange = (newLocationId) => {
  const locationChanged =
    String(newLocationId ?? '') !== String(selectedLocationId ?? '');

  setSelectedLocationId(newLocationId);

  if (locationChanged) {
    clearSearchFilters();
  }
};





  const displayNext = (dateVal, overrideVal) => {
  const o = (overrideVal ?? '').toString().trim();
  return dateVal || (o.length ? o : '—');
};
  // Fetch areas when location changes
useEffect(() => {
  if (selectedLocationId) {
    fetchAreas();
    setSearchTriggered(true);
  } else {
    setAreas([]);
    setAreaIdNameMap({});
    setEquipmentData([]);
    setTotalPages(0);
    setTotalElements(0);
  }
  // eslint-disable-next-line
}, [selectedLocationId]);

// Fetch equipment on search, paging, sorting, or area change
useEffect(() => {
  if (!selectedLocationId) return;
  fetchEquipment();
  // eslint-disable-next-line
}, [searchTriggered, currentPage, sortBy, sortDirection, pageSize, selectedAreaIds, selectedCoveredProcesses, showOutOfService]);

const handleExportAll = async () => {
  try {
    let url = `/api/equipment/descriptions/search-all?locationId=${selectedLocationId}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
    if (selectedAreaIds.length > 0) selectedAreaIds.forEach(id => { url += `&areaIds=${id}`; });
    if (searchEquipmentName) url += `&equipmentName=${encodeURIComponent(searchEquipmentName)}`;
    if (searchEquipmentNumber) url += `&equipmentNumber=${encodeURIComponent(searchEquipmentNumber)}`;
    if (searchRiskRating) url += `&riskRating=${encodeURIComponent(searchRiskRating)}`;
    if (searchPastDue) url += `&pastDue=true`;
    if (selectedCoveredProcesses.length > 0) {
        selectedCoveredProcesses.forEach(p => {
          url += `&coveredProcesses=${encodeURIComponent(p)}`;
        });
      }
      if (!showOutOfService) {
  url += `&inServiceOnly=true`;
}

    const response = await axios.get(url);
    const equipment = Array.isArray(response.data) ? response.data : [];

    if (equipment.length === 0) {
      alert('There are no assets to export for the selected filters.');
      return;
    }

    const data = equipment.map(eq => ({
      'Equipment Name': eq.equipmentName,
      'Equipment Number': eq.equipmentNumber,
      'Alternate Number': eq.alternateNumber,
      'Area': areaIdNameMap[eq.areaId] || '—',
      'Equipment Type': eq.equipmentType,
      'Risk Rating': eq.riskRating,
      'Material Type': eq.materialType,
      'Next External Inspection': displayNext(eq.nextExternalInspection, eq.nextExternalOverride),
      'Next Internal Inspection': displayNext(eq.nextInternalInspection, eq.nextInternalOverride),
      'Next UT Inspection': displayNext(eq.nextUtInspection, eq.nextUtOverride)
    }));

    const ws = XLSX.utils.json_to_sheet(data);

    if (data.length > 0) {
  const headers = Object.keys(data[0]);
  const lastCol = headers.length - 1;
  const lastRow = data.length; // header is row 0, last data row is row = data.length

  const ref = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastRow, c: lastCol },
  });

  ws['!autofilter'] = { ref };
}

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Assets");
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'assets_database.xlsx');
  } catch (error) {
    console.error('Export failed:', error);
    alert('Unable to export the asset database. Please try again.');
  }
};




const handleExportRoutineInServiceToExcel = async () => {
  try {
    const params = new URLSearchParams();
    selectedAreaIds.forEach((id) => params.append('areaIds', String(id)));
    const query = params.toString();
    const url = `/api/routine-inspections/export/location/${selectedLocationId}${query ? `?${query}` : ''}`;
    // fetch filtered data
    const res = await axios.get(url);
    const data = Array.isArray(res.data) ? res.data : [];

    if (!data.some((group) => Array.isArray(group.inspections) && group.inspections.length > 0)) {
      alert('There are no routine inspections to export for the selected filters.');
      return;
    }

    const wb = XLSXStyle.utils.book_new();
    const usedSheetNames = new Set();

    const grayCell = {
      fill: {
        patternType: 'solid',
        fgColor: { rgb: 'D9D9D9' }, // Light gray
      },
      font: {
        bold: true,
      },
    };
    const blackCell = {
      fill: {
        patternType: 'solid',
        fgColor: { rgb: '000000' }, // black
      }
    };
    

    data.forEach((group, groupIndex) => {
      if (!Array.isArray(group.inspections) || group.inspections.length === 0) return;
      const sheetData = [];

      group.inspections.forEach((item) => {
        // Static info rows
        sheetData.push([
          { v: 'Inspector:', s: grayCell },
          { v: item.inspector || '—' }
        ]);
        sheetData.push([
          { v: 'Location:', s: grayCell },
          { v: item.locationNumber || '—' }
        ]);
        sheetData.push([
          { v: 'Equipment ID:', s: grayCell },
          { v: item.equipmentNumber || '—' }
        ]);
        sheetData.push([
          { v: 'Equipment Description:', s: grayCell },
          { v: item.equipmentName || '—' }
        ]);
        sheetData.push([
          { v: 'Routine Inspection Date:', s: grayCell },
          { v: item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString('en-US') : '—' }
        ]);

        // Spacer
        sheetData.push(['', '', '', '', '']);

        // Checklist header
        sheetData.push([
          { v: '#', s: grayCell },
          { v: 'Checklist Item', s: grayCell },
          { v: 'Completed', s: grayCell },
          { v: 'Abnormal Condition Notes', s: grayCell }
        ]);

        // Checklist rows
        const checklistRows = [
          [1, 'Asset Signage (Present / Legible)', item.signageOk ? '✔' : '✘', item.signageNotes || ''],
          [2, 'Check for signs of leakage', item.leakageOk ? '✔' : '✘', item.leakageNotes || ''],
          [3, 'Check for shell distortion & excessive corrosion', item.shellCorrosionOk ? '✔' : '✘', item.shellCorrosionNotes || ''],
          [4, 'Check condition of coating (corrosion, scaling, pitting)', item.coatingOk ? '✔' : '✘', item.coatingNotes || ''],
          [5, 'Signs of settlement / sagging', item.settlingOk ? '✔' : '✘', item.settlingNotes || ''],
          [6, 'Check condition of Foundation (cracks, spalling, exposed rebar)', item.foundationOk ? '✔' : '✘', item.foundationNotes || ''],
          [7, 'Insulation System: (Damage / penetrations, Missing jacketing / insulation, etc)', item.insulationOk ? '✔' : '✘', item.insulationNotes || ''],
          [8, 'Check condition of overpressure protection / relief devices', item.overpressureOk ? '✔' : '✘', item.overpressureNotes || ''],
          [9, 'Check condition of Secondary Containment', item.containmentOk ? '✔' : '✘', item.containmentNotes || ''],
          [10, 'Check condition of grounding cable', item.groundingOk ? '✔' : '✘', item.groundingNotes || ''],
          [11, 'Condition of ladders, handrails & platforms', item.laddersOk ? '✔' : '✘', item.laddersNotes || ''],
        ];

       

        

        checklistRows.forEach(row => sheetData.push(row));

        // Spacer row between equipment blocks
        sheetData.push([
          { v: '', s: blackCell },
          { v: '', s: blackCell },
          { v: '', s: blackCell },
          { v: '', s: blackCell }
        ]);
      });

     

      const ws = XLSXStyle.utils.aoa_to_sheet(sheetData);

      // Optional: Widen all columns
      ws['!cols'] = Array(4).fill({ wch: 40 });

      const baseName = String(group.areaName || `Area ${groupIndex + 1}`)
        .replace(/[\\/?*[\]:]/g, '-')
        .trim()
        .slice(0, 31) || `Area ${groupIndex + 1}`;
      let sheetName = baseName;
      let suffix = 2;
      while (usedSheetNames.has(sheetName)) {
        const suffixText = ` (${suffix})`;
        sheetName = `${baseName.slice(0, 31 - suffixText.length)}${suffixText}`;
        suffix += 1;
      }
      usedSheetNames.add(sheetName);
      XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);
    });

    const excelBuffer = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'routine_in_service_inspections.xlsx');
  } catch (err) {
    console.error('Excel export failed:', err);
    alert('Unable to export routine inspections. Please try again.');
  }
};







  const fetchAreas = async () => {
    try {
      const response = await axios.get(`/api/areas/location/${selectedLocationId}`);
      const data = Array.isArray(response.data) ? response.data : [];
      setAreas(data);
  
      // Build map: areaId -> areaName
      const map = {};
      data.forEach(area => {
        map[area.id] = area.name;
      });
      setAreaIdNameMap(map);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  const handleSort = (field) => {
    setCurrentPage(0);
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };
  
  

  const fetchEquipment = async () => {
    try {
      let url = `/api/equipment/descriptions/search-paginated?locationId=${selectedLocationId}&page=${currentPage}&size=${pageSize}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
  
      if (selectedAreaIds.length > 0) {
        selectedAreaIds.forEach(id => {
          url += `&areaIds=${id}`;
        });
      }
      if (searchEquipmentName) url += `&equipmentName=${encodeURIComponent(searchEquipmentName)}`;
      if (searchEquipmentNumber) url += `&equipmentNumber=${encodeURIComponent(searchEquipmentNumber)}`;
      if (searchRiskRating) url += `&riskRating=${encodeURIComponent(searchRiskRating)}`;
      if (searchPastDue) url += `&pastDue=true`;
      if (selectedCoveredProcesses.length > 0) {
        selectedCoveredProcesses.forEach(p => {
          url += `&coveredProcesses=${encodeURIComponent(p)}`;
        });
      }
      if (!showOutOfService) {
      url += `&inServiceOnly=true`;
    }
  
      const response = await axios.get(url);
      setEquipmentData(Array.isArray(response.data?.content) ? response.data.content : []);
      setTotalPages(Number.isInteger(response.data?.totalPages) ? response.data.totalPages : 0);
      setTotalElements(Number.isFinite(response.data?.totalElements) ? response.data.totalElements : 0);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setEquipmentData([]);
      setTotalPages(0);
      setTotalElements(0);
    }
  };
  
  
  
  const handleRowClick = (equipmentId) => {
  navigate(`/equipment/${equipmentId}/profile`, {
    state: {
      fromAssetsDatabase: true,
      assetsFilters: {
        selectedLocationId,
        selectedAreaIds,
        searchEquipmentName,
        searchEquipmentNumber,
        searchRiskRating,
        searchPastDue,
        showOutOfService,
        selectedCoveredProcesses,
      },
    },
  });
};
  

  return (
    <div className="dashboard-page assets-database-page">
      {/* Top Navbar */}
      <div className="dashboard-topbar">
        <div className="topbar-left">
          <img src={trident} alt="ODIN Logo" className="navbar-logo" />
          <CompanyLocationSelector
  onLocationChange={handleLocationChange}
/>
        </div>
        <div className="topbar-center">
          <span className="page-title">Assets Database</span>
        </div>
        <div className="topbar-right">
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        <Sidebar />

        <main className="dashboard-content assets-database-content">
          <header className="assets-page-heading">
            <span className="assets-page-eyebrow">Asset intelligence</span>
            <h1>Assets Database</h1>
            <p>Search, review, and export the equipment records for the selected location.</p>
          </header>

          <section className="assets-filter-panel" aria-labelledby="asset-filter-heading">
            <div className="assets-section-heading">
              <div>
                <h2 id="asset-filter-heading">Search and filters</h2>
                <p>Narrow the inventory by equipment details, inspection status, process, or area.</p>
              </div>
              <button type="button" className="assets-secondary-button" onClick={clearSearchFilters}>
                Clear filters
              </button>
            </div>

            <div className="equipment-search-fields">
              <label className="assets-field">
                <span>Equipment name</span>
                <input
                  type="text"
                  placeholder="Search by name"
                  value={searchEquipmentName}
                  onChange={e => setSearchEquipmentName(e.target.value)}
                />
              </label>
              <label className="assets-field">
                <span>Equipment number</span>
                <input
                  type="text"
                  placeholder="Search by number"
                  value={searchEquipmentNumber}
                  onChange={e => setSearchEquipmentNumber(e.target.value)}
                />
              </label>
              <label className="assets-field">
                <span>Risk rating</span>
                <input
                  type="text"
                  placeholder="Search by rating"
                  value={searchRiskRating}
                  onChange={e => setSearchRiskRating(e.target.value)}
                />
              </label>

              <div className="assets-filter-actions">
                <button
                  type="button"
                  className="assets-primary-button"
                  onClick={() => {
                    setCurrentPage(0);
                    setSearchTriggered(prev => !prev);
                  }}
                >
                  Search assets
                </button>
              </div>
            </div>

            <div className="assets-toggle-row">
              <label className={`assets-toggle${searchPastDue ? ' selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={searchPastDue}
                  onChange={e => {
                    setSearchPastDue(e.target.checked);
                    setCurrentPage(0);
                    setSearchTriggered(prev => !prev);
                  }}
                />
                <span>Past due inspections only</span>
              </label>
              <label className={`assets-toggle${showOutOfService ? ' selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={showOutOfService}
                  onChange={e => {
                    setShowOutOfService(e.target.checked);
                    setCurrentPage(0);
                    setSearchTriggered(prev => !prev);
                  }}
                />
                <span>Include out-of-service assets</span>
              </label>
            </div>

            <div className="assets-filter-group">
              <div className="assets-filter-group-heading">
                <h3>Covered processes</h3>
                <span>{selectedCoveredProcesses.length > 0 ? `${selectedCoveredProcesses.length} selected` : 'All processes'}</span>
              </div>
              <div className="covered-process-options">
                {COVERED_PROCESS_OPTIONS.map(option => {
                  const isSelected = selectedCoveredProcesses.includes(option);
                  return (
                    <label key={option} className={`covered-process-option${isSelected ? ' selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          setCurrentPage(0);
                          setSelectedCoveredProcesses(prev => (
                            prev.includes(option)
                              ? prev.filter(process => process !== option)
                              : [...prev, option]
                          ));
                        }}
                      />
                      <span>{option}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {areas.length > 0 && (
              <div className="assets-filter-group assets-area-filter">
                <AreaMultiSelect
                  areas={areas}
                  selectedAreaIds={selectedAreaIds}
                  onChange={(areaIds) => {
                    setCurrentPage(0);
                    setSelectedAreaIds(areaIds);
                  }}
                />
              </div>
            )}
          </section>

          <section className="assets-results-toolbar" aria-label="Asset results and exports">
            <div className="assets-result-count">
              <span>Current results</span>
              <strong>{totalElements.toLocaleString()} {totalElements === 1 ? 'asset' : 'assets'}</strong>
            </div>
            <div className="export-buttons-row">
              <button className="export-btn" disabled={!selectedLocationId} onClick={handleExportAll}>Export assets</button>
              <button className="export-btn secondary" disabled={!selectedLocationId} onClick={handleExportRoutineInServiceToExcel}>
                Export routine inspections
              </button>
            </div>
          </section>

          <section className="assets-table-panel" aria-label="Assets database results">
            <div className="table-scroll-container">
              <table className="equipment-table">
                <thead>
                  <tr>
                    {ASSET_TABLE_COLUMNS.map(([field, label]) => {
                      const isActiveSort = sortBy === field;
                      return (
                        <th
                          key={field}
                          aria-sort={isActiveSort ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          <button
                            type="button"
                            className={`assets-table-sort${isActiveSort ? ' active' : ''}`}
                            onClick={() => handleSort(field)}
                          >
                            <span>{label}</span>
                            <span className="assets-table-sort-icon" aria-hidden="true">
                              {isActiveSort ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {equipmentData.length > 0 ? equipmentData.map(eq => (
                    <tr
                      key={eq.equipmentId}
                      className="clickable-row"
                      onClick={() => handleRowClick(eq.equipmentId)}
                    >
                      <td className="assets-equipment-name">{eq.equipmentName || '—'}</td>
                      <td>{eq.equipmentNumber || '—'}</td>
                      <td>{eq.alternateNumber || '—'}</td>
                      <td>{areaIdNameMap[eq.areaId] || '—'}</td>
                      <td>{eq.equipmentType || '—'}</td>
                      <td><span className="assets-risk-rating">{eq.riskRating || '—'}</span></td>
                      <td>{eq.materialType || '—'}</td>
                      <td>
                        <span className={eq.outOfService ? 'status-pill status-oos' : 'status-pill status-in-service'}>
                          {eq.outOfService ? 'Out of Service' : 'In Service'}
                        </span>
                      </td>
                      <td>{displayNext(eq.nextExternalInspection, eq.nextExternalOverride)}</td>
                      <td>{displayNext(eq.nextInternalInspection, eq.nextInternalOverride)}</td>
                      <td>{displayNext(eq.nextUtInspection, eq.nextUtOverride)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td className="assets-table-empty" colSpan={ASSET_TABLE_COLUMNS.length}>
                        {selectedLocationId
                          ? 'No assets match the selected filters.'
                          : 'Select a location to view its asset inventory.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <footer className="pagination-controls assets-pagination">
              <div className="assets-pagination-navigation">
                <button disabled={currentPage === 0} onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                <span>Page {totalElements === 0 ? 0 : currentPage + 1} of {totalElements === 0 ? 0 : totalPages}</span>
                <button disabled={currentPage >= totalPages - 1 || totalElements === 0} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
              </div>
              <span className="assets-pagination-summary">
                Showing {totalElements === 0 ? 0 : (currentPage * pageSize) + 1}–{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements}
              </span>
              <label className="assets-page-size">
                <span>Rows per page</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(0);
                  }}
                >
                  {[10, 25, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
            </footer>
          </section>
        </main>
      </div>

     
    </div>
  );
};

export default AssetsDatabase;
